import type { ReactNode } from 'react';

// Enums (use string values for JSON compatibility)
export enum AccountStatus {
    OK = 'OK',
    Warning = 'Warning',
    Suspended = 'Suspended',
    Banned = 'Banned'
}

export enum ProductCondition {
    New = 'New',
    LikeNew = 'LikeNew',
    Good = 'Good',
    Fair = 'Fair',
    Poor = 'Poor'
}

export enum ProductStatus {
    PendingReview = 'PendingReview',
    Active = 'Active',
    Rejected = 'Rejected',
    Sold = 'Sold',
    Archived = 'Archived'
}

export enum OrderStatus {
    Pending = 'Pending',
    Paid = 'Paid',
    Processing = 'Processing',
    Shipped = 'Shipped',
    InTransit = 'InTransit',
    Delivered = 'Delivered',
    Problem = 'Problem',
    Cancelled = 'Cancelled',
    Refunded = 'Refunded',
    Completed = 'Completed'
}

export enum PaymentStatus {
    Pending = 'Pending',
    Authorized = 'Authorized',
    Captured = 'Captured',
    Failed = 'Failed',
    Refunded = 'Refunded'
}

export interface StrikeDto {
    id?: string; // GUID as string
    reason: string;
    created?: string; // Date as ISO string
}

export interface SellerRatingDto {
    stars: number;
    comment?: string;
    orderId: number;
}

export interface SellerReviewDto {
    id: string;
    stars: number;
    comment?: string;
    created: string;
    authorDisplayName: string;
    authorAvatarUrl?: string;
    productTitle?: string;
    productId?: number;
}

export interface SellerRatingBreakdownDto {
    averageRating: number;
    ratingCount: number;
    /** Counts for 1★ … 5★ */
    countsByStar: number[];
}

export interface PendingRatingDto {
    orderId: number;
    productTitle?: string;
    productImageUrl?: string;
    sellerId: string;
    sellerName?: string;
}

export interface OrderRatingStatusDto {
    hasRated: boolean;
    canRate: boolean;
}

export interface PublicUserProfileDto {
    id: string;
    displayName: string;
    userName?: string;
    showPersonLine?: boolean;
    profilePictureUrl?: string;
    region?: string;
    city?: string;
    memberSince: string;
    averageRating: number;
    ratingCount: number;
    activeListingsCount: number;
    itemsSoldCount: number;
    followerCount?: number;
    isFollowing?: boolean;
}

export interface FollowFeedItemDto {
    eventId: number;
    productId: number;
    productTitle: string;
    primaryImageUrl?: string;
    price: number;
    sellerId: string;
    sellerDisplayName: string;
    sellerUserName?: string;
    sellerProfilePictureUrl?: string;
    listedAt: string;
    productStatus: string;
    isSold: boolean;
}

export interface FollowFeedPageDto {
    items: FollowFeedItemDto[];
    unreadCount: number;
    feedSeenAt?: string | null;
}

export interface FollowPreferencesDto {
    followingDigestEmailsEnabled: boolean;
    newFollowerDigestEmailsEnabled?: boolean;
}

export interface FollowingUserDto {
    id: string;
    displayName: string;
    userName?: string;
    showPersonLine?: boolean;
    profilePictureUrl?: string;
    activeListingsCount: number;
    followedAt: string;
}

export interface CreateTicketDto {
    orderId?: number;
    subject: string;
    message: string;
}

export interface ReportDto {
    reason: string;
    description?: string;
    productId?: number;
    sellerId?: string;
}

export interface SellerSummaryDto {
    averageRating: number;
    ratingCount: number;
}

export interface User {
    id: string;
    email: string;
    userName?: string;
    isAdmin: boolean;
    roles?: string[];
    status: AccountStatus;
    strikes: StrikeDto[];
    // Add more fields like address if needed
}

// Public Product type (for home/my-products)
export interface Product {
    id: number;
    sellerFirstName: string;
    title: string;
    description: string;
    price: number;
    imageUrls: string[];
    status: ProductStatus;
    isSold?: boolean;
    createdAt?: string;
    sellerId?: string;
    condition: ProductCondition;
    quantityAvailable: number;
    categories: string[];
    tags: string[];
    specifications: { [category: string]: { [specName: string]: string } }; // Now nested
    viewCount: number;
    /** Number of users who favourited this product (populated on my-products). */
    favouriteCount?: number;
    averageSellerRating: number;
    sellerRatingCount: number;
    sellerRegion: string;
    /** Public city for location search (never street/postal). */
    sellerCity?: string;
    /** Active unpaid checkout hold (15 min). Live from API on every catalog fetch. */
    isReserved?: boolean;
    /** ISO timestamp when the hold ends. */
    reservedUntil?: string | null;
    /** Seconds left at response time (server clock) — prefer for countdown. */
    holdRemainingSeconds?: number | null;
    /** Viewer holds this reservation (Continue checkout). */
    reservedByMe?: boolean;
    /** Package size band (S/M/L/XL…) for shipping fee + labels. */
    packageSizeBandCode?: string | null;
    packageSizeBandLabel?: string | null;
    /** Buyer shipping amount for this product's band (EUR). */
    shippingBuyerAmount?: number | null;
    /** Seller last attested the listing against the terms (edit form). */
    listingAttestedAt?: string | null;
    allowsLocalPickup?: boolean;
    pickupCities?: string[];
    pickupLocationIds?: number[];
}

/** Shop-safe package size band (listing form + shipping display). */
export interface ShippingSizeBandPublicDto {
    code: string;
    label: string;
    sortOrder: number;
    buyerAmount: number;
    helpText?: string | null;
    maxWeightGrams?: number | null;
    maxLengthCm?: number | null;
    maxWidthCm?: number | null;
    maxHeightCm?: number | null;
    allowsPlatformLabel: boolean;
}

export interface ShippingSizeBandAdminDto {
    id: number;
    code: string;
    label: string;
    sortOrder: number;
    isActive: boolean;
    buyerAmount: number;
    shippingTitle?: string | null;
    sendcloudShippingMethodId?: number | null;
    nominalWeightGrams: number;
    maxWeightGrams?: number | null;
    maxLengthCm?: number | null;
    maxWidthCm?: number | null;
    maxHeightCm?: number | null;
    helpText?: string | null;
    allowsPlatformLabel: boolean;
}

export interface UpsertShippingSizeBandDto {
    code: string;
    label: string;
    sortOrder: number;
    isActive: boolean;
    buyerAmount: number;
    shippingTitle?: string | null;
    sendcloudShippingMethodId?: number | null;
    nominalWeightGrams: number;
    maxWeightGrams?: number | null;
    maxLengthCm?: number | null;
    maxWidthCm?: number | null;
    maxHeightCm?: number | null;
    helpText?: string | null;
    allowsPlatformLabel: boolean;
}

// For user create/update (public)
export type CreateProductDto = {
    title: string;
    price: number;
    description: string;
    imageUrls: string[];
    condition?: ProductCondition; // Enum
    quantityAvailable?: number;
    categories?: string[];
    tags?: string[];
    specifications?: { [category: string]: { [specName: string]: string } };
    /** Required package size band code (Vinted-style). */
    packageSizeBandCode: string;
    /** Seller confirms the listing matches the terms. */
    listingAttested?: boolean;
    pickupLocationIds?: number[];
};

export type UpdateProductDto = {
    title?: string;
    price?: number;
    description?: string;
    imageUrls: string[];
    condition?: ProductCondition;
    quantityAvailable?: number;
    categories?: string[];
    tags?: string[];
    specifications?: { [category: string]: { [specName: string]: string } };
    packageSizeBandCode?: string;
    listingAttested?: boolean;
    pickupLocationIds?: number[];
};

/** Ephemeral label OCR suggestions (never product image URLs). */
export type ListingAssistResultDto = {
    suggestedTitle?: string | null;
    descriptionHint?: string | null;
    fields: Record<string, string>;
    rawLines?: string[];
    source?: string;
};

export interface Order {
    id: number;
    buyerId: string;
    productId: number;
    shopifyOrderId: string;
    status: OrderStatus; // Enum
    total: number;
    // New
    orderDate: string; // ISO
    shippedDate?: string;
    paymentMethod?: string;
    paymentStatus: PaymentStatus; // Enum
    shippingCost?: number;
    shippingMethod?: string;
    trackingNumber?: string | null;
    trackingCompany?: string | null;
    trackingUrl?: string | null;
    trackingUpdatedAt?: string | null;
    isDisputed: boolean;
    adminNotes?: string;
    ShippingRecipientName?: string;
    shippingStreetAddress?: string;
    shippingCity?: string;
    shippingPostalCode?: string;
    shippingCountry?: string;
    shippingProvider?: string | null;
    labelPdfUrl?: string | null;
    labelCreatedAt?: string | null;
    isViewerSeller?: boolean;
    canShip?: boolean;
    labelsAvailable?: boolean;
    product?: Product;
    hasRated?: boolean;
    productTitleSnapshot?: string | null;
    primaryImageUrlSnapshot?: string | null;
    /** Pending hold still within 15‑min window (server). */
    isLiveCheckout?: boolean;
    /** ISO end of live hold; null when not live. */
    reservedUntil?: string | null;
    /** Seconds left at response time (server clock) — prefer for countdown. */
    holdRemainingSeconds?: number | null;
    /** Unpaid hold ended → Payment not completed. */
    isPaymentNotCompleted?: boolean;
}

export interface ShipOrderDto {
    carrier: string;
    trackingNumber: string;
    trackingUrl?: string;
}

export interface CreateCheckoutItemDto {
    productId: number;
    quantity?: number;
}

export interface CreateCheckoutDto {
    items: CreateCheckoutItemDto[];
    customerNotes?: string;
    customerEmail?: string;
    childId?: number;
}

export interface CheckoutResponseDto {
    orderId: number;
    invoiceUrl: string;
    shopifyDraftOrderName: string;
}

// FUTURE: buyer–seller chat DTOs (re-enable with ChatBox + /api/chat)
// export interface Message {
//     id: number;
//     content: string;
//     senderId: string;
//     receiverId?: string;
//     productId: number;
//     sentAt?: string;
// }
//
// export interface ChatBoxProps {
//     productId: number;
//     otherUserId: string;
// }

export interface ProtectedRouteProps {
    children: ReactNode;
    // requireAdmin is used by AdminProtectedRoute to gate the admin dashboard.
    // Do not remove — admin routes rely on it.
    requireAdmin?: boolean;
}

export interface PreservedArray<T> {
    $values: T[];
}

/**
 * Shop branding (`GET /api/sitesettings` via shopAxios).
 * No tax / shipping / platform fee.
 */
export interface PublicSiteSettings {
    id: number;
    textColor: string;
    backgroundColor: string;
    accentColor: string;
    highlightColor?: string;
    backgroundImageUrl?: string | null;
    contactEmail?: string;
    facebookUrl?: string;
    instagramUrl?: string;
    tikTokUrl?: string;
    pinterestUrl?: string;
    allCategoriesIcon?: string;
    welcomeHeroImageUrl?: string | null;
    welcomeHowItWorksPath?: string;
}

/** Admin branding only (`GET/PUT /api/admin/sitesettings` via adminAxios). */
export type SiteSettings = PublicSiteSettings;

/**
 * Shop locale (`GET /api/marketplace/locale` via shopAxios).
 * Country + optional domestic shipping amount for PDP hints.
 * Never platform fee % tiers.
 */
export interface ShopMarketplaceLocale {
    defaultCountryCode: string;
    /** When true, show soft shipping amount on PDP. */
    shippingFeeEnabled?: boolean;
    /** Domestic shipping amount when shipping fee is on. */
    domesticShippingAmount?: number | null;
}

/** Admin economics (`GET/PUT /api/admin/marketplace-economics` via adminAxios). */
export interface MarketplaceEconomicsDto {
    id: number;
    defaultCountryCode: string;
    currencyCode: string;
    taxReportingEnabled: boolean;
    taxReportingScheme: string;
    taxItemThreshold: number;
    taxAmountThreshold: number;
    taxYearTimeZoneId: string;
    shippingFeeEnabled: boolean;
    domesticShippingAmount: number;
    domesticShippingTitle: string;
    platformFeeEnabled: boolean;
    platformFeeTitle: string;
}

/** Admin-only platform fee price band (% of list). Never exposed on shop APIs. */
export interface PlatformFeeTierDto {
    id: number;
    minPrice: number;
    maxPrice: number | null;
    percentage: number;
    sortOrder: number;
    isActive: boolean;
    label?: string | null;
}

export interface UpsertPlatformFeeTierDto {
    minPrice: number;
    maxPrice: number | null;
    percentage: number;
    sortOrder: number;
    isActive: boolean;
    label?: string | null;
}

export interface PlatformFeeRevenueDto {
    totalPlatformFees: number;
    orderCount: number;
    currencyCode: string;
}

export interface PlatformFeePreviewDto {
    itemPrice: number;
    feeAmount: number;
    percentageApplied: number;
    applied: boolean;
    title: string;
}

export enum SidebarFilterType {
    Price = 'Price',
    Condition = 'Condition',
    Specification = 'Specification',
    GrowthPhase = 'GrowthPhase'
}

export interface SidebarFilterOptionDto {
    id: number;
    value: string;
    checklistPhaseId?: number;
    minAgeMonths?: number;
    maxAgeMonths?: number;
    labelTranslationKey: string;
    icon?: string;
    isEnabled: boolean;
    sortOrder: number;
    minPrice?: number;
    maxPrice?: number;
}

export interface SidebarFilterDto {
    id: number;
    type: SidebarFilterType;
    typeName: string;
    specificationId?: number;
    specificationName?: string;
    isEnabled: boolean;
    sortOrder: number;
    titleTranslationKey?: string;
    rangeMin?: number;
    rangeMax?: number;
    rangeStep?: number;
    options: SidebarFilterOptionDto[];
}

export interface AdminSidebarFilterListDto {
    id: number;
    type: SidebarFilterType;
    typeName: string;
    specificationId?: number;
    specificationName?: string;
    isEnabled: boolean;
    sortOrder: number;
    optionCount: number;
}

export interface CreateSidebarFilterDto {
    type: SidebarFilterType;
    specificationId?: number;
    isEnabled?: boolean;
    sortOrder?: number;
    titleTranslationKey?: string;
    rangeMin?: number;
    rangeMax?: number;
    rangeStep?: number;
}

export interface UpdateSidebarFilterOptionDto {
    id: number;
    icon?: string;
    labelTranslationKey?: string;
    isEnabled: boolean;
    sortOrder: number;
    minPrice?: number;
    maxPrice?: number;
}

export interface UpdateSidebarFilterDto {
    isEnabled: boolean;
    sortOrder: number;
    titleTranslationKey?: string;
    rangeMin?: number;
    rangeMax?: number;
    rangeStep?: number;
    options: UpdateSidebarFilterOptionDto[];
}

export interface PartnerInquiryListDto {
    id: number;
    companyName: string;
    contactName: string;
    email: string;
    phone?: string;
    message: string;
    inquiryType: string;
    createdAt: string;
}

export interface AdminTicketListDto {
    id: number;
    userId: string;
    userEmail?: string | null;
    userName?: string | null;
    orderId?: number | null;
    subject: string;
    message: string;
    status: string;
    createdAt: string;
}

export interface CreatePartnerInquiryDto {
    companyName: string;
    contactName: string;
    email: string;
    phone?: string;
    message: string;
    inquiryType?: string;
}

export interface ProductFilterParams {
    minPrice?: number;
    maxPrice?: number;
    condition?: ProductCondition;
    conditions?: ProductCondition[];
    specFilters?: string[];
    growthPhaseId?: number;
    growthPhaseIds?: number[];
    ageMinMonths?: number;
    ageMaxMonths?: number;
}

export interface ColorPickerProps {
    value: string;
    onChange: (value: string) => void;
    onCancel: () => void;
    onSave: (value: string) => void;
}

export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    page: number;
    limit: number;
    filter?: string;       // new
    top?: number;          // new
    category?: string;
    dataVersion?: number;  // server version stamp for lightweight cache invalidation
}

// New DTOs for API Responses/Inputs
export interface LoginDto {
    email: string;
    password: string;
}

export interface RegisterDto {
    email: string;
    password: string;
    userName: string;
    firstName: string;
    lastName: string;
    preferredLanguage?: string;
}

export interface UserProfileDto {
    id: string;
    email: string;
    userName: string;
    firstName?: string;
    lastName?: string;
    status: AccountStatus;
    strikes: StrikeDto[];
    shippingStreetAddress: string;
    shippingCity: string;
    shippingPostalCode: string;
    shippingCountry: string;
    isFlagged: boolean;
    hasProvidedTaxInfo: boolean; // New
    taxId?: string; // New
    profilePictureUrl?: string;
    preferredLanguage?: string;
    /** When true, home/search hide the current user's own listings. */
    excludeOwnProducts?: boolean;
    /**
     * Server-side: Home coachmark tour finished or skipped.
     * Cross-device; set once — do not re-write on every step or help retrigger.
     */
    productTourHomeCompleted?: boolean;
    /**
     * Server-side: My Petito coachmark tour finished or skipped.
     * Cross-device; set once — do not re-write on every step or help retrigger.
     */
    productTourMyPetitoCompleted?: boolean;
    childDataConsentAt?: string | null;
    myPetitoSnoozedUntil?: string | null;
    myPetitoNudgesDisabled?: boolean;
    /** When the shopper last finished an on-login prompt. The next one waits the admin gap after this. */
    lastLoginPromptClosedAt?: string | null;
}

export interface UpdateUserProfileDto {
    userName?: string;
    firstName?: string;
    lastName?: string;
    currentPassword?: string;
    newPassword?: string;
    confirmNewPassword?: string; // Client validation
    shippingStreetAddress?: string;
    shippingCity?: string;
    shippingPostalCode?: string;
    shippingCountry?: string;
    taxId?: string; // New
    profilePictureUrl?: string;
    preferredLanguage?: string;
    excludeOwnProducts?: boolean;
}
export interface AnnualStatsDto {
    year: number;
    itemsSold: number;
    totalEuro: number;
    isFlagged: boolean;
}
export interface AdminAnnualStatsDto {
    year: number;
    itemsSold: number;
    totalEuro: number;
    isFlagged: boolean;
}

export interface AdminAuthResponseDto {
    id: string;
    email: string;
    userName: string;
    isAdmin: boolean;
    roles: string[];
}

export interface NotificationDto {
    id: string;           // GUID as string
    message: string;
    link?: string | null;
    createdAt: string;    // ISO string
    isRead: boolean;
}

//admin DTO's
export interface AdminUserListDto {
    id: string;
    email: string;
    userName: string;
    isAdmin: boolean;
    roles: string[];
    status: AccountStatus;
    strikeCount: number;
    //isLockedOut: boolean;
    isFlagged: boolean;
    hasProvidedTaxInfo: boolean; // New
}

export interface AdminUserDetailDto extends AdminUserListDto {
    createdDate: string; // ISO string
    lockoutEndDate?: string;
    twoFactorEnabled: boolean;
    emailConfirmed: boolean;
    strikes: StrikeDto[]; // Full list
    // New fields
    lockoutEnabled: boolean;
    phoneNumber?: string;
    streetAddress?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    taxId?: string; // New
    hasProvidedTaxInfo: boolean;
    itemsSoldThisYear: number;
    totalEuroThisYear: number;
    annualStats?: AdminAnnualStatsDto[];
    reports: AdminReportDto[];
}

export interface AdminCreateUserDto {
    email: string;
    password: string;
    userName: string;
    isAdmin: boolean;
    roles: string[];
    // New fields (optional for create)
    lockoutEnabled?: boolean;
    twoFactorEnabled?: boolean;
    phoneNumber?: string;
    streetAddress?: string;
    city?: string;
    postalCode?: string;
    country?: string;
}

export interface AdminUpdateUserDto {
    email?: string;
    userName?: string;
    password?: string;
    isAdmin?: boolean;
    roles?: string[];
    lockoutEnabled?: boolean;
    // New fields (optional for partial updates)
    twoFactorEnabled?: boolean;
    lockoutEnd?: string | null; // ISO string for manual lockout end
    phoneNumber?: string;
    streetAddress?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    isFlagged: boolean;
    hasProvidedTaxInfo: boolean;
    taxId?: string;
}

// Admin DTOs
export interface AdminProductListDto {
    id: number;
    title: string;
    price: number;
    sellerId?: string;
    isSold: boolean;
    status: ProductStatus;
    condition: ProductCondition;
    viewCount: number;
}

export interface AdminProductDetailDto extends AdminProductListDto {
    description: string;
    imageUrls: string[];
    quantityAvailable: number;
    tags: string[];
    averageRating: number;
    reviewCount: number;
    createdAt: string;
    updatedAt?: string;
    categories?: string[];
    specifications?: { [category: string]: { [specName: string]: string } }; // Nested now
    packageSizeBandCode?: string | null;
}

export interface AdminCreateProductDto {
    sellerId: string; // Required
    title: string; // Required
    price: number; // Required
    description: string; // Required
    imageUrls: string[];
    condition: ProductCondition; // Required
    quantityAvailable: number; // Required
    categories?: string[];
    tags?: string[];
    specifications?: { [category: string]: { [specName: string]: string } };
    packageSizeBandCode?: string;
}

export interface AdminUpdateProductDto {
    title?: string;
    price?: number;
    description?: string;
    imageUrls: string[];
    sellerId?: string;
    isSold?: boolean;
    condition?: ProductCondition;
    status?: ProductStatus;
    quantityAvailable?: number;
    tags?: string[];
    categories?: string[];
    specifications?: { [category: string]: { [specName: string]: string } }; // Nested now
    packageSizeBandCode?: string;
}

export interface AdminOrderListDto {
    id: number;
    buyerId: string;
    productId: number;
    shopifyOrderId: string;
    status: OrderStatus;
    total: number;
    isDisputed: boolean;
}

export interface AdminOrderDetailDto extends AdminOrderListDto {
    orderDate: string;
    shippedDate?: string;
    paymentMethod?: string;
    paymentStatus: PaymentStatus;
    shippingCost: number;
    shippingMethod?: string;
    trackingNumber?: string;
    adminNotes?: string;
    shippingStreetAddress?: string;
    shippingCity?: string;
    shippingPostalCode?: string;
    shippingCountry?: string;
}

export interface AdminCreateOrderDto {
    buyerId: string; // Required
    productId: number; // Required
    shopifyOrderId: string;
    total: number; // Required
    status?: OrderStatus;
    paymentMethod?: string;
    paymentStatus?: PaymentStatus;
    shippingCost?: number;
    shippingMethod?: string;
    trackingNumber?: string;
    isDisputed?: boolean;
    adminNotes?: string;
    shippingStreetAddress?: string;
    shippingCity?: string;
    shippingPostalCode?: string;
    shippingCountry?: string;
}

export interface AdminUpdateOrderDto {
    shopifyOrderId?: string;
    total?: number;
    status?: OrderStatus;
    shippedDate?: string;
    paymentMethod?: string;
    paymentStatus?: PaymentStatus;
    shippingCost?: number;
    shippingMethod?: string;
    trackingNumber?: string;
    isDisputed?: boolean;
    adminNotes?: string;
    shippingStreetAddress?: string;
    shippingCity?: string;
    shippingPostalCode?: string;
    shippingCountry?: string;
    buyerId?: string;
    productId?: number;
}

//reports
export interface AdminReportDto {
    id: string;
    reason: string;
    description?: string;
    productId?: number;
    created: string;           // ISO string from backend
    status: string;            // "Pending" or "Reviewed"
    adminNotes?: string;
}

export enum SpecificationType {
    String = 'String',
    Integer = 'Integer',
    Decimal = 'Decimal',
    Boolean = 'Boolean',
    Enum = 'Enum',
    Date = 'Date'
}

export interface SpecificationDto {
    id: number;
    name: string;
    type: SpecificationType;
    isRequired: boolean;
    categories?: string[];
    options?: string;
}

export interface CreateSpecificationDto {
    name: string;
    type: SpecificationType;
    isRequired: boolean;
    options?: string;
}

export interface UpdateSpecificationDto {
    name?: string;
    type?: SpecificationType;
    isRequired?: boolean;
    options?: string;
}

export interface CategoryDto {
    id: number;
    name: string;
    specifications: string[]; // Spec names for frontend
    icon?: string;
    /** Default package size band for new listings in this category. */
    defaultPackageSizeBandCode?: string | null;
    /** Optional minimum package size band (empty = no floor). */
    minPackageSizeBandCode?: string | null;
}
export interface CreateCategoryDto {
    name: string;
    icon?: string;
    defaultPackageSizeBandCode?: string | null;
    minPackageSizeBandCode?: string | null;
}

export interface UpdateCategoryDto {
    name?: string;
    icon?: string;
    defaultPackageSizeBandCode?: string | null;
    minPackageSizeBandCode?: string | null;
}

export interface ChildDto {
    id: number;
    name: string;
    birthDate?: string | null; // ISO string
    expectedBirthDate?: string | null;
    heightCm?: number | null;
    weightKg?: number | null;
    measurementsUpdatedAt?: string | null;
    suggestedFitAgeMonths?: number | null;
    suggestedHeightCm?: number | null;
    suggestedWeightKg?: number | null;
    measurementsStale?: boolean;
    checklistItems: ChecklistItemWithPhase[];
    gender: Gender;
}

export interface CreateChildDto {
    name: string;
    birthDate?: string | null;
    expectedBirthDate?: string | null;
    heightCm?: number | null;
    weightKg?: number | null;
    gender: Gender;
    childDataConsent?: boolean;
}

export interface UpdateChildDto extends CreateChildDto { }

export interface AdminChecklistItem {
    id: number;
    name: string;
    isChecked: boolean;
    //category?: string;
}

export interface AdminChecklistTemplate {
    id: number;
    /** Fallback / list display name (preferred language). */
    name: string;
    /** i18n key checklist.template.{id}.name */
    nameKey?: string;
    /** Per-language names keyed by language code (nl-be, fr-be, en, …). */
    names?: Record<string, string>;
    phaseIds: number[];
    //category?: string;
}

export interface AdminCreateChecklistTemplateDto {
    /** Per-language names; at least one non-empty required. */
    names: Record<string, string>;
    phaseIds: number[];
}

export interface AdminUpdateChecklistTemplateDto extends AdminCreateChecklistTemplateDto { }

export enum Gender {
    Boy = 'Boy',
    Girl = 'Girl',
    PreferNotToSay = 'PreferNotToSay'
}

/** Shop structure for My Journey educational cards (copy via translation keys). */
export interface PhaseHighlightVisual {
    sortOrder: number;
    isVisible: boolean;
    emoji?: string;
    textKey: string;
}

export interface PhaseVisualContent {
    /** i18n key for short name under age range (e.g. Newborn) */
    displayNameKey?: string;
    showWhatsHappening: boolean;
    whatsHappeningEmoji?: string;
    whatsHappeningTitleKey?: string;
    whatsHappeningBodyKey?: string;
    showDevelopmentHighlights: boolean;
    highlights: PhaseHighlightVisual[];
    showTip: boolean;
    tipEmoji?: string;
    tipTextKey?: string;
    /** Soft phase tint hex for journey chrome */
    accentColor?: string;
}

// NEW: ChecklistPhase (clinically correct age bands from CDC)
export interface ChecklistPhase {
    id: number;
    name: string;
    minAgeMonths: number;
    maxAgeMonths: number;
    clinicalSource: string;
    icon?: string;
    /** Educational Figma-style cards; null if unconfigured */
    content?: PhaseVisualContent | null;
}

// ── ADMIN: My Journey designer (strict admin types) ──
export interface AdminMyRebabyPhaseListDto {
    phaseId: number;
    name: string;
    minAgeMonths: number;
    maxAgeMonths: number;
    icon?: string;
    hasContent: boolean;
    contentUpdatedAt?: string;
}

export interface AdminMyRebabyHighlightDto {
    sortOrder: number;
    isVisible: boolean;
    emoji?: string;
    text: Record<string, string>;
}

export interface AdminMyRebabyPhaseDetailDto {
    phaseId: number;
    name: string;
    minAgeMonths: number;
    maxAgeMonths: number;
    icon?: string;
    /** Short Figma-style name under age range (lang → text) */
    displayName: Record<string, string>;
    showWhatsHappening: boolean;
    whatsHappeningEmoji?: string;
    whatsHappeningTitle: Record<string, string>;
    whatsHappeningBody: Record<string, string>;
    showDevelopmentHighlights: boolean;
    highlights: AdminMyRebabyHighlightDto[];
    showTip: boolean;
    tipEmoji?: string;
    tipText: Record<string, string>;
    accentColor?: string;
    updatedAt?: string;
}

export interface AdminUpdateMyRebabyPhaseContentDto {
    icon?: string | null;
    displayName: Record<string, string>;
    showWhatsHappening: boolean;
    whatsHappeningEmoji?: string | null;
    whatsHappeningTitle: Record<string, string>;
    whatsHappeningBody: Record<string, string>;
    showDevelopmentHighlights: boolean;
    highlights: AdminMyRebabyHighlightDto[];
    showTip: boolean;
    tipEmoji?: string | null;
    tipText: Record<string, string>;
    accentColor?: string | null;
}

// Checklist item carries its phases (for Relevant/Upcoming/Previous logic and per-phase progress).
// Custom items are phase-specific (they are created with the phase they belong to).
export interface ChecklistItemWithPhase {
    id: number;
    name: string;
    /** i18n key for template-backed items; null/undefined for custom items. */
    nameKey?: string | null;
    isChecked: boolean;
    isCustom?: boolean;
    phases: ChecklistPhase[];
}

// ── ADMIN-ONLY TYPES (strict separation) ──
export interface AdminChecklistPhaseDto {
    id: number;
    name: string;
    minAgeMonths: number;
    maxAgeMonths: number;
    icon?: string;
}

// ── Growth Insight (smart recommendation based on height/weight) ──
export interface GrowthInsightDto {
    hasRecommendation: boolean;
    message: string;
    messageKey?: string;
    recommendedPhaseId?: number;
    recommendedPhaseName: string;
}
//shopify types
export interface ShopifyWebhookStatus {
    success: boolean;
    subscriptions: Record<string, boolean>;
    allGood: boolean;
    message?: string;
}

// ── Translation Admin DTOs (for the admin panel) ──
export interface TranslationAdminDto {
    id: number;
    key: string;
    description?: string;
    translations: Record<string, string>;
    updatedAt: string;
}

export interface CreateTranslationDto {
    key: string;
    description?: string;
    translations: Record<string, string>;
}

export interface UpdateTranslationDto {
    description?: string;
    translations: Record<string, string>;
}
//admin languages
export interface LanguageAdminDto {
    code: string;
    name: string;
    translationKey: string;
    nativeName: string;
    flagEmoji: string;
    isActive: boolean;
    sortOrder: number;
    
}
export interface LanguageDto {
    code: string;
    name: string;
    translationKey: string;
    nativeName?: string;
    flagEmoji?: string;
    isActive: boolean;
}